import { create } from 'zustand';
import { auth, loadTokens, clearTokens, setTokens } from '../lib/api';

export const useAuthStore = create((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isAdmin: false,
  theme: 'system',
  measurementSystem: 'imperial',

  initialize: async () => {
    const { accessToken } = loadTokens();

    if (!accessToken) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }

    try {
      const user = await auth.getMe();
      set({
        user,
        isAuthenticated: true,
        isAdmin: user.isAdmin || false,
        isLoading: false,
        theme: user.theme || 'system',
        measurementSystem: user.measurementSystem || 'imperial',
      });
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      clearTokens();
      set({ isLoading: false, isAuthenticated: false });
    }
  },

  setAuth: (user, accessToken, refreshToken) => {
    setTokens(accessToken, refreshToken);
    set({
      user,
      isAuthenticated: true,
      isAdmin: user.isAdmin || false,
      theme: user.theme || 'system',
      measurementSystem: user.measurementSystem || 'imperial',
    });
  },

  login: async (email, password) => {
    const data = await auth.login(email, password);
    set({
      user: data.user,
      isAuthenticated: true,
      isAdmin: data.user.isAdmin || false,
      theme: data.user.theme || 'system',
      measurementSystem: data.user.measurementSystem || 'imperial',
    });
    return data.user;
  },

  register: async (email, password, name) => {
    const data = await auth.register(email, password, name);
    // If pending approval, data.user will be undefined
    if (data.pending) {
      return { pending: true, message: data.message };
    }
    set({
      user: data.user,
      isAuthenticated: true,
      isAdmin: data.user.isAdmin || false,
      theme: data.user.theme || 'system',
      measurementSystem: data.user.measurementSystem || 'imperial',
    });
    return data.user;
  },

  googleLogin: async (idToken) => {
    const data = await auth.googleLogin(idToken);
    set({
      user: data.user,
      isAuthenticated: true,
      isAdmin: data.user.isAdmin || false,
      theme: data.user.theme || 'system',
      measurementSystem: data.user.measurementSystem || 'imperial',
    });
    return data.user;
  },

  logout: async () => {
    await auth.logout();
    set({
      user: null,
      isAuthenticated: false,
      isAdmin: false,
      theme: 'system',
      measurementSystem: 'imperial',
    });
  },

  updateSettings: async (settings) => {
    const updatedUser = await auth.updateSettings(settings);
    set({
      user: updatedUser,
      theme: updatedUser.theme || get().theme,
      measurementSystem: updatedUser.measurementSystem || get().measurementSystem,
    });
    return updatedUser;
  },

  setTheme: (theme) => {
    set({ theme });
  },

  setMeasurementSystem: (system) => {
    set({ measurementSystem: system });
  },

  deleteAccount: async () => {
    await auth.deleteAccount();
    set({
      user: null,
      isAuthenticated: false,
      theme: 'system',
      measurementSystem: 'imperial',
    });
  },
}));
